import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard, Key, Link2, Webhook, Save, TestTube, Eye, EyeOff,
  Copy, Check, ExternalLink, Loader2, Shield, CheckCircle2, XCircle, AlertCircle
} from 'lucide-react';

export default function AdminPaymentGateway() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState('');

  const [apiKey, setApiKey] = useState('');
  const [apiBaseUrl, setApiBaseUrl] = useState('https://coinpayup.com/api');
  const [isActive, setIsActive] = useState(false);
  const [detectedWallet, setDetectedWallet] = useState(null); // { address, network }

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/payment-gateway`, { headers });
      setConfig(data);
      setApiKey(data.api_key || '');
      setApiBaseUrl(data.api_base_url || 'https://coinpayup.com/api');
      setIsActive(data.is_active || false);
      if (data.wallet_address) {
        setDetectedWallet({ address: data.wallet_address, network: data.wallet_network || 'bsc' });
      }
    } catch {
      toast.error('Failed to load gateway config');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (newVal) => {
    setIsActive(newVal);
    try {
      await axios.put(`${API}/admin/payment-gateway`, {
        api_key: apiKey,
        api_base_url: apiBaseUrl,
        is_active: newVal,
      }, { headers });
      toast.success(newVal ? 'Gateway enabled' : 'Gateway disabled');
    } catch {
      setIsActive(!newVal);
      toast.error('Failed to update');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/payment-gateway`, {
        api_key: apiKey,
        api_base_url: apiBaseUrl,
        is_active: isActive,
      }, { headers });
      setConfig(data);
      if (data.wallet_detected) {
        setDetectedWallet({ address: data.wallet_address, network: data.wallet_network });
        toast.success(`Settings saved — receiving wallet auto-detected: ${data.wallet_address.slice(0, 10)}...${data.wallet_address.slice(-6)}`);
      } else if (data.wallet_error) {
        setDetectedWallet(null);
        toast.error(data.wallet_error);
      } else {
        toast.success('Payment gateway settings saved');
      }
      setTestResult(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/payment-gateway/test`, {}, { headers });
      setTestResult(data);
      if (data.status === 'success') toast.success(data.message);
      else toast.error(data.message);
    } catch (err) {
      setTestResult({ status: 'failed', message: err.response?.data?.detail || 'Test failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(label);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(''), 2000);
  };

  if (loading) {
    return (
      <AdminLayout title="Payment Gateway">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  const callbackUrl = config?.callback_url || 'https://coinpayup.com/api/gateway/callback';
  const webhookUrl = config?.webhook_url || 'https://coinpayup.com/api/gateway/webhook';

  return (
    <AdminLayout title="Payment Gateway">
      <div className="max-w-3xl space-y-6" data-testid="payment-gateway-page">

        {/* Status Banner */}
        <div className={`flex items-center gap-3 p-4 rounded-xl border ${isActive ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-muted/30 border-border'}`} data-testid="gateway-status-banner">
          <CreditCard className={`h-5 w-5 ${isActive ? 'text-emerald-500' : 'text-muted-foreground'}`} />
          <div className="flex-1">
            <p className={`text-sm font-semibold ${isActive ? 'text-emerald-500' : 'text-muted-foreground'}`}>
              CoinPayUp Payment Gateway
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isActive ? 'Active — merchants can make payments' : 'Inactive — gateway is disabled'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{isActive ? 'Active' : 'Inactive'}</span>
            <Switch
              checked={isActive}
              onCheckedChange={handleToggleActive}
              data-testid="gateway-active-toggle"
            />
          </div>
        </div>

        {/* API Configuration */}
        <Card data-testid="api-config-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Key className="h-4 w-4 text-primary" />
              API Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">API Key</label>
              <div className="relative">
                <Input
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="cpk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="pr-20 font-mono text-sm"
                  data-testid="gateway-api-key-input"
                />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setShowKey(!showKey)}>
                    {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </Button>
                  {apiKey && (
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyToClipboard(apiKey, 'API Key')}>
                      {copied === 'API Key' ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Your CoinPayUp API key. Get it from Merchant Dashboard &gt; API Keys.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">API Base URL</label>
              <Input
                value={apiBaseUrl}
                onChange={(e) => setApiBaseUrl(e.target.value)}
                placeholder="https://coinpayup.com/api"
                className="font-mono text-sm"
                data-testid="gateway-api-base-url"
              />
            </div>

            {/* Receiving Wallet — auto-detected from API key */}
            <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                <p className="text-sm font-semibold text-emerald-400">Receiving Wallet (Auto-Detected)</p>
              </div>
              <p className="text-xs text-muted-foreground">
                The receiving wallet is automatically resolved from your API key — no manual entry needed.
                Save your API key above to detect the wallet.
              </p>
              {detectedWallet ? (
                <div className="flex items-center gap-2 text-xs text-emerald-400 mt-1">
                  <Check className="h-3.5 w-3.5" />
                  <span className="font-mono">{detectedWallet.address}</span>
                  <span className="text-muted-foreground">({detectedWallet.network?.toUpperCase()})</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-amber-400 mt-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  No wallet detected yet — save a valid API key to auto-detect
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button onClick={handleSave} disabled={saving} data-testid="gateway-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Settings
              </Button>
              <Button variant="outline" onClick={handleTest} disabled={testing || !apiKey} data-testid="gateway-test-btn">
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TestTube className="h-4 w-4 mr-2" />}
                Test Connection
              </Button>
            </div>

            {testResult && (
              <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${testResult.status === 'success' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`} data-testid="gateway-test-result">
                {testResult.status === 'success' ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
                {testResult.message}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Callback & Webhook URLs */}
        <Card data-testid="urls-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="h-4 w-4 text-primary" />
              Callback & Webhook URLs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block flex items-center gap-2">
                <Webhook className="h-3.5 w-3.5 text-muted-foreground" />
                Callback URL
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2.5 bg-muted/50 rounded-lg border border-border font-mono text-sm text-foreground break-all">
                  {callbackUrl}
                </div>
                <Button size="icon" variant="outline" className="shrink-0" onClick={() => copyToClipboard(callbackUrl, 'Callback URL')}>
                  {copied === 'Callback URL' ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Set this as the callback URL in your CoinPayUp merchant settings. Payment confirmations will be sent here.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block flex items-center gap-2">
                <Webhook className="h-3.5 w-3.5 text-muted-foreground" />
                Webhook URL
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2.5 bg-muted/50 rounded-lg border border-border font-mono text-sm text-foreground break-all">
                  {webhookUrl}
                </div>
                <Button size="icon" variant="outline" className="shrink-0" onClick={() => copyToClipboard(webhookUrl, 'Webhook URL')}>
                  {copied === 'Webhook URL' ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Webhook endpoint for real-time payment event notifications (payment.confirmed, payment.failed, etc.)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Integration Guide */}
        <Card data-testid="integration-guide-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Integration Guide
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
                <p>Create an API key from <strong className="text-foreground">Merchant Dashboard &gt; API Keys</strong> and paste it above.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
                <p>The <strong className="text-foreground">Receiving Wallet</strong> is automatically detected from the API key — the first active wallet of the API key's merchant account will be used to receive all billing payments.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
                <p>Copy the <strong className="text-foreground">Callback URL</strong> above and set it in your CoinPayUp merchant webhook settings.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">4</span>
                <p>Click <strong className="text-foreground">Test Connection</strong> to verify the API key works.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">5</span>
                <p>Enable the gateway toggle. Merchants will see a <strong className="text-foreground">Pay with CoinPayUp</strong> button on their billing page.</p>
              </div>
            </div>

            <div className="pt-3 border-t border-border">
              <p className="text-xs font-medium text-foreground mb-2">API Endpoints Used:</p>
              <div className="space-y-1.5 font-mono text-xs">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-500 rounded text-[10px] font-bold">POST</span>
                  <span>/api/invoices</span>
                  <span className="text-muted-foreground/60">— Create payment invoice</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-500 rounded text-[10px] font-bold">GET</span>
                  <span>/api/invoices/:id/status</span>
                  <span className="text-muted-foreground/60">— Check payment status</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span className="px-1.5 py-0.5 bg-amber-500/10 text-amber-500 rounded text-[10px] font-bold">POST</span>
                  <span>/api/gateway/callback</span>
                  <span className="text-muted-foreground/60">— Receives payment confirmations</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </AdminLayout>
  );
}
